import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import api from '../../lib/api';
import { fromSlug, jobUrlSegment } from '../../lib/slug';
import { Invitation } from '../../types';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Textarea from '../../components/ui/Textarea';
import { formatDate } from '../../lib/utils';

function inviteStatusPill(status: string) {
  const s = status.toUpperCase();
  if (s === 'ACCEPTED') return 'status-pill status-pill-pos';
  if (['PENDING', 'VIEWED', 'MODIFICATION_REQUESTED'].includes(s)) return 'status-pill status-pill-warn';
  if (['DECLINED', 'REJECTED', 'WITHDRAWN', 'CANCELLED'].includes(s)) return 'status-pill status-pill-danger';
  return 'status-pill status-pill-muted';
}

export default function InvitationDetailPage() {
  const { id: rawId } = useParams();
  const id = fromSlug(rawId || '');
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminContext = location.pathname.startsWith('/admin');
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');

  useEffect(() => {
    loadInvitation();
  }, [id]);

  const loadInvitation = async () => {
    try {
      const response = await api.get(`/invitations/${id}`);
      setInvitation(response.data);
    } catch (error) {
      console.error('Failed to load invitation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRespondToModification = async (action: 'approve' | 'reject' | 'pending') => {
    try {
      setResponding(true);
      await api.post(`/invitations/${id}/respond-to-modification`, {
        action,
        message: responseMessage,
      });
      setResponseMessage('');
      await loadInvitation();
      alert(`Modification request ${action}d successfully!`);
    } catch (error) {
      console.error('Failed to respond to modification:', error);
      alert('Failed to respond to modification request');
    } finally {
      setResponding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-glass-text-2">
        Loading...
      </div>
    );
  }

  if (!invitation || !invitation.job) {
    return (
      <div className="text-center py-12 text-glass-text-3">Invitation not found</div>
    );
  }

  const job = invitation.job;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() =>
            isAdminContext ? navigate('/admin/invitations') : navigate(`/agency/campaigns/${jobUrlSegment(job)}`)
          }
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="page-title">Invitation Details</h1>
          <p className="text-glass-text-2 text-sm mt-1">{job.title}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>KOL Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-glass-text-2">Name</span>
                  <div className="font-medium text-glass-text">
                    {invitation.kolProfile?.name}
                  </div>
                </div>
                <div>
                  <span className="text-sm text-glass-text-2">Email</span>
                  <div className="font-medium text-glass-text">
                    {invitation.kolProfile?.email}
                  </div>
                </div>
                <div>
                  <span className="text-sm text-glass-text-2">Status</span>
                  <div className="mt-1">
                    <span className={inviteStatusPill(invitation.status)}>
                      {invitation.status}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Modification Request Actions */}
          {invitation.status === 'MODIFICATION_REQUESTED' && (
            <Card className="border-amber-400/30 bg-amber-400/5">
              <CardHeader>
                <CardTitle className="text-amber-500 dark:text-amber-300 flex items-center gap-2">
                  <RefreshCw className="w-5 h-5" />
                  Modification Request
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-glass-3 p-4 rounded-xl border border-glass">
                  <p className="text-sm text-glass-text mb-2">
                    <strong>{invitation.kolProfile?.name}</strong> has requested
                    modifications to this invitation.
                  </p>
                  <p className="text-xs text-glass-text-2">
                    Check the messages below for details about what changes they're
                    requesting.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-glass-text mb-2">
                    Response Message (Optional)
                  </label>
                  <Textarea
                    value={responseMessage}
                    onChange={(e) => setResponseMessage(e.target.value)}
                    placeholder="Add a message to the KOL about your decision..."
                    rows={3}
                    className="w-full"
                  />
                </div>

                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={() => handleRespondToModification('approve')}
                    disabled={responding}
                    className="flex items-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Approve & Accept
                  </Button>
                  <Button
                    onClick={() => handleRespondToModification('pending')}
                    disabled={responding}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Revert to Pending
                  </Button>
                  <Button
                    onClick={() => handleRespondToModification('reject')}
                    disabled={responding}
                    variant="outline"
                    className="flex items-center gap-2 text-rose-500 dark:text-rose-300"
                  >
                    <XCircle className="w-4 h-4" />
                    Reject
                  </Button>
                </div>

                <div className="text-xs text-glass-text-2 bg-glass-3 p-3 rounded-xl border border-glass">
                  <strong className="text-glass-text">What do these actions do?</strong>
                  <ul className="mt-2 space-y-1 list-disc list-inside">
                    <li>
                      <strong>Approve & Accept:</strong> Accept the invitation with the
                      KOL's requested changes
                    </li>
                    <li>
                      <strong>Revert to Pending:</strong> Send back to pending for the
                      KOL to review again
                    </li>
                    <li>
                      <strong>Reject:</strong> Decline the invitation entirely
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          )}

          {invitation.messages && invitation.messages.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Messages</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {invitation.messages.map((message) => (
                    <div
                      key={message.id}
                      className="p-3 bg-glass-2 rounded-xl border border-glass"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-glass-text">
                          {message.senderUser?.fullName ||
                            message.senderUser?.email ||
                            'Anonymous'}
                        </span>
                        <span className="text-xs text-glass-text-3">
                          {formatDate(message.createdAt)}
                        </span>
                      </div>
                      <p className="text-sm text-glass-text-2">{message.body}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Timeline</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <div className="text-sm text-glass-text-2">Sent</div>
                <div className="font-medium text-glass-text">
                  {formatDate(invitation.createdAt)}
                </div>
              </div>
              {invitation.status !== 'PENDING' && (
                <div>
                  <div className="text-sm text-glass-text-2">Responded</div>
                  <div className="font-medium text-glass-text">
                    {formatDate(invitation.updatedAt)}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Flags</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-glass-text-2">Shortlisted</span>
                <Badge variant={invitation.isShortlisted ? 'success' : 'secondary'}>
                  {invitation.isShortlisted ? 'Yes' : 'No'}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-glass-text-2">Approved</span>
                <Badge
                  variant={invitation.isApprovedForProduction ? 'success' : 'secondary'}
                >
                  {invitation.isApprovedForProduction ? 'Yes' : 'No'}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
